import React, {
  useContext, useMemo, useState,
} from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import Heading from '../../../generic-components/Heading';
import { NUMBER_TO_DAY_MAP } from '../services';
import { useStore } from '../../../store';
import { useSetUpBucketMutation } from '../Hooks/useSetUpBucketMutation';
import { FlowContext } from '../context';
import { STEPS, STYLES } from '../consts';
import { USER_MESSAGE_TYPES } from '../../../flows/UserMessageFlow/constants';
import { POPUP_LIFESPAN } from '../../../flows/emergencyBucketDashboard/constants';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';

export const SetupBucket = () => {
  const [isAmountSetupComplete, setIsAmountSetupComplete] = useState<boolean>(false);
  const summaryData = useStore(state => state.getSummaryData());
  const { emergencyBucketResponse } = summaryData;
  const context = useContext(FlowContext);
  const { setMessageObject } = useStore(state => state);
  const [amount, setAmount] = useState<string>(emergencyBucketResponse?.weeklyAmount ? JSON.stringify(emergencyBucketResponse?.weeklyAmount) : '');
  const { t } = useTranslation();

  const { mutateAsync, isLoading } = useSetUpBucketMutation();
  const { refetch: refreshSummaryData, isRefetching } = useAccountSummaryQuery();

  const isLimitReached = useMemo(() => {
    const { dailyLimit } = emergencyBucketResponse;
    return Number(amount) > dailyLimit;
  }, [amount]);

  const isFormValid = useMemo(() => {
    if (!Number(amount)) {
      return false;
    }
    return !isLimitReached;
  }, [amount, isLimitReached]);

  const handleAmountChange = (value:string) => {
    setAmount(value);
  };

  const handleSetAmount = () => {
    setIsAmountSetupComplete(true);
  };

  const handleConfirmSetup = async () => {
    await mutateAsync({ savingAmount: Number(amount) });
    await refreshSummaryData();
    setMessageObject({
      type: USER_MESSAGE_TYPES.UPPER_MESSAGE,
      useLottie: true,
      iconSource: '',
      messageText: `${t('SAVINGS_BUCKET.SETUP_BUCKET.UM_PRE')} $${amount} ${t('SAVINGS_BUCKET.SETUP_BUCKET.UM_POST')}`,
      autoCloseTimeout: POPUP_LIFESPAN,
      callback: () => {
        setMessageObject({ type: USER_MESSAGE_TYPES.ZERO_STATE });
      },
    });
    context?.changeActiveStep(STEPS.DASHBOARD);
  };

  return (
    <div className={STYLES.SETUP_BUCKET.wrapper}>
      <Heading title={`${isAmountSetupComplete ? t('SAVINGS_BUCKET.SETUP_BUCKET.ALL_SET') : t('SAVINGS_BUCKET.SETUP_BUCKET.START_SAVING')} `} className={STYLES.SETUP_BUCKET.mainTitle} />
      <Heading title={`${isAmountSetupComplete ? t('SAVINGS_BUCKET.SETUP_BUCKET.DISCLAIMER_1') : t('SAVINGS_BUCKET.SETUP_BUCKET.DISCLAIMER_2')}`} className={STYLES.SETUP_BUCKET.subTitle} />
      {!isAmountSetupComplete && (
      <CurrencyInput
        className={`${STYLES.SETUP_BUCKET.currencyInput} ${isLimitReached ? 'border-red02 focus:border-red02' : ''}`}
        placeholder='$0.00'
        defaultValue={emergencyBucketResponse?.weeklyAmount}
        decimalsLimit={2}
        prefix='$'
        autoFocus
        onValueChange={(value) => handleAmountChange(value || '0')}
        id='CurrencyInput'
      />
      )}
      {isAmountSetupComplete && <Heading title={`$${amount}`} className={STYLES.SETUP_BUCKET.amount} />}
      {isLimitReached ? <Heading title={t('SAVINGS_BUCKET.SETUP_BUCKET.LIMIT_REACH')} className={STYLES.SETUP_BUCKET.inputErrorMessage} /> : <Heading title={`Every ${NUMBER_TO_DAY_MAP[2 as keyof typeof NUMBER_TO_DAY_MAP]}`} className={STYLES.SETUP_BUCKET.feePeriodDisclaimer} />}
      {!isAmountSetupComplete && <LiliButton disabled={!isFormValid} onClick={handleSetAmount} className='mb-[12px]' title={t('SAVINGS_BUCKET.SETUP_BUCKET.SET_AMOUNT_CTA')} id='setAmountCta' />}
      {isAmountSetupComplete && <LiliButton isLoading={isLoading || isRefetching} onClick={handleConfirmSetup} className='mb-[12px]' title={t('SAVINGS_BUCKET.SETUP_BUCKET.CONFIRM')} id='confirmSetup' />}
      <Heading type='HTML' title={t('SAVINGS_BUCKET.SETUP_BUCKET.FOOTER_DISCLAIMER')} className={STYLES.SETUP_BUCKET.disclaimer} />
    </div>
  );
};
